import { Avatar } from "@radix-ui/themes";
import { User } from "models/User";
import { useEffect, useMemo, useRef } from "react";
import { BsPersonFillSlash } from "react-icons/bs";
import { RxPerson } from "react-icons/rx";
import { useSelector } from "react-redux";
import { RootState } from "store";
import CrownGl from "../../../assets/images/crown_gl.png";

export const ParticipantGroupPanel = ({ table_name, participants }) => {
  const profileData = useSelector((state: any) => state?.auth?.user as User);
  return (
    <div className="flex flex-col w-full overflow-x-clip">
      <div className="flex w-full justify-center text-app-blue p-[1px] bg-app-yellow">
        <label>{table_name}</label>
      </div>
      <div className="w-full my-2">
        <ParticipantTile
          participants={participants}
          radius={"none"}
          size={undefined}
        />
      </div>
    </div>
  );
};

export const ParticipantTile = ({
  participants,
  radius,
  size,
  showKebabMenu = false,
}: any) => {
  const profileData = useSelector((state: any) => state?.auth?.user as User);
  const consumers: any = useSelector(
    (state: RootState) => state.live.settings.consumers
  );
  const producers = useSelector(
    (state: RootState) => state.live.settings.producers
  );
  const groupLeader = useSelector((state: any) => state?.live?.settings?.groupLeaderNew);

  const myEmail = profileData?.email;
  const tableData = participants?.[0]; //  Always one object
  const seats = tableData?.seats || [];

  const myTableEmails = useMemo(() => {
    if (!myEmail || !seats.length) return [];
    const tableEmails = seats.map((seat: any) => seat.participant_email);
    return tableEmails.includes(myEmail) ? tableEmails : [];
  }, [seats, myEmail]);
  const leaderMap = useMemo(() => {
    const map = new Map<string, boolean>();
    if (Array.isArray(groupLeader)) {
      groupLeader.forEach((grp: any) => {
        grp.seats?.forEach((seat: any) => {
          map.set(seat.participant_email, seat.group_leader);
        });
      });
    }
    return map;
  }, [groupLeader]);
  const getTrack = (email: string) => {
    if (!consumers || !producers || !profileData) {
      console.warn("No consumers, producers, or profileData available");
      return { audio: null, video: null };
    }

    if (email === profileData.email) {
      const validProducers = Object.values(producers).filter(Boolean);
      const videoProducer: any = validProducers.find(
        (producer: any) => producer?.kind === "video"
      );
      const audioProducer: any = validProducers.find(
        (producer: any) => producer?.kind === "audio"
      );

      return {
        audio: audioProducer
          ? { track: audioProducer.track, locallyPaused: audioProducer.paused }
          : null,
        video: videoProducer
          ? { track: videoProducer.track, locallyPaused: videoProducer.paused }
          : null,
      };
    }

    let audioConsumer = null;
    let videoConsumer = null;

    for (const id in consumers) {
      const consumer = consumers[id];
      if (consumer.peerId === email && consumer.track) {
        if (consumer.kind === "audio") audioConsumer = consumer;
        else if (consumer.kind === "video" && !consumer.appData?.share)
          videoConsumer = consumer;
      }
    }

    const audio =
       audioConsumer
        ? { track: audioConsumer.track, locallyPaused: audioConsumer.remotelyPaused }
        : null;

    const video = videoConsumer
      ? { track: videoConsumer.track, locallyPaused: videoConsumer.remotelyPaused }
      : null;

    return { audio, video };
  };

  return (
    <div className="grid grid-cols-2 w-full h-full gap-2">
      {seats.map((seat: any) => {
        const track = getTrack(seat.participant_email);
        const hasStream =
          track &&
          ((track.video && track.video.track) ||
            (track.audio && track.audio.track));
        const isGroupLeader = leaderMap.get(seat.participant_email) || false;

        return (
          <div
            key={seat.participant_id}
            className="w-full h-full flex justify-center items-center"
          >
            {hasStream ? (
              <Participant
                name={seat.participant_name}
                groupLeader={isGroupLeader}
                streamArray={track}
                radius={radius}
                size={size}
                kebab={showKebabMenu}
              />
            ) : (
              <NoParticipantPanel
                name={seat.participant_name}
                groupLeader={isGroupLeader}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};


export const NoParticipantPanel = ({ name, height = 130, width = 192, groupLeader = false }: any) => {
  const isMicOn = false;
  const isVideoOn = false;
  const getDimension = (dim: number | string | undefined, isWidth: boolean = false): string => {
    if (dim === undefined) return isWidth ? `${width}px` : `${height}px`;
    if (typeof dim === 'number') return `${dim}px`;
    return dim;
  };

  const containerWidth = getDimension(width, true);
  const containerHeight = getDimension(height);
  const getTailwindSize = (size: number | string, isWidth: boolean): string => {
    if (typeof size === 'string') return size; // Assume it's a Tailwind class
    const sizeMap: { [key: number]: string } = isWidth
      ? {
        192: 'w-48',
        128: 'w-32',
        256: 'w-64',
        320: 'w-80',
        384: 'w-96',
      }
      : {
        144: 'h-36',
        96: 'h-24',
        192: 'h-48',
        240: 'h-60',
        288: 'h-72',
      };
    return sizeMap[size] || (isWidth ? 'w-48' : 'h-36');
  };

  const containerClasses = [
    'relative flex justify-center items-center',
    getTailwindSize(width, true),
    getTailwindSize(height, false),
    'mobile:w-36 mobile:h-20',
    'tablet:w-40 tablet:h-24',
    'laptop:w-48 laptop:h-28',
    'desktop:w-48 desktop:h-28',
    'largescreen:w-48 largescreen:h-28',
    'relative bg-[#5B5D6B] bg-opacity-40 rounded-lg overflow-hidden flex items-center justify-center gap-2',
  ].join(' ');

  return (
    <div className={containerClasses}>
      {groupLeader && <img
        src={CrownGl}
        alt="Group Leader Crown"
        className="absolute -top-[2%] z-10 object-contain left-[10px]"
        style={{ height: '25px', width: '25px' }}
      />}
      <BsPersonFillSlash className="text-5xl mobile:text-3xl text-white mb-1" />
      <div className="absolute mobile:text-s bottom-1 right-1 px-0.5 py-0.5 rounded text-white text-s truncate max-w-[60%]">
        {name || 'Trainee'}
      </div>
      <div className="absolute mobile:text-s bottom-1 left-1 px-0.5 py-0.5 rounded text-yellow-500 text-s truncate max-w-[100%]">
        Yet to Join
      </div>
    </div>
  );
};

const Participant = ({ profilePhoto, name, streamArray, groupLeader = false }: any) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const startConsumer = async (consumer: any, ref: React.RefObject<HTMLVideoElement | HTMLAudioElement>) => {
    if (!consumer || !consumer.track || !(consumer.track instanceof MediaStreamTrack)) {
      console.warn(`Invalid consumer for ${consumer?.kind}:`, consumer);
      return;
    }

    try {
      const stream = new MediaStream([consumer.track.clone()]);
      if (ref.current) {
        ref.current.srcObject = stream;
      }
    } catch (error) {
      console.error(`Error creating MediaStream for ${consumer.kind}:`, error);
    }
  };

  useEffect(() => {
    if (streamArray?.video && streamArray.video.track?.readyState === "live") {
      startConsumer(streamArray.video, videoRef);
    }
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
  }, [streamArray?.video?.track]);
  return (
    <div className="flex relative flex-col items-center w-full h-28 mobile:h-20 tablet:h-24 laptop:h-28 desktop:h-28 largescreen:h-28">
      {groupLeader && <img
        src={CrownGl}
        alt="Group Leader Crown"
        className="absolute -top-[2%] z-10 object-contain left-[10px]"
        style={{ height: '25px', width: '25px' }}
      />}
      {streamArray?.video?.track?.readyState === 'live' ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="object-cover rounded w-full h-full"
        />
      ) : (
        <div className="w-full h-full" style={{ borderRadius: '8px', background: '#5B5D6B' }}>
          <Avatar
            src={profilePhoto}
            fallback={name ? name?.charAt(0) : "T"}
            size="7"
            className="full-width-avatar"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              background: 'radial-gradient(50% 86.76% at 50% 50%, rgba(33, 32, 37, 0.00) 0%, rgba(33, 32, 37, 0.30) 50%, rgba(33, 32, 37, 0.60) 100%)',
              backgroundBlendMode: 'multiply',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFF',
              fontSize: '20px',
              fontWeight: '700',
            }}
          />
        </div>
      )}
      <div className="absolute bottom-0 mobile:text-s right-1 px-2 py-0.5 rounded text-white text-xs truncate max-w-[70%]">
        {name}
      </div>
    </div>
  );
};

const NoProfilePhoto = ({ name }: any) => {
  // const lastName = name?.split(" ").pop();
  // const initials = (name?.charAt(0) + lastName?.charAt(0)).toUpperCase();

  return (
    <div className="flex flex-col items-center w-[180px] h-[130px]">
      <Avatar
        fallback={name ? name?.charAt(0) : "T"}
        size="7"
        radius="none"
        color="lime"
        className="mb-1"
      />
      <p className="text-xs text-white truncate w-full text-center">
        {name}
      </p>
    </div>
  );
};

const NoParticipant = (name) => {
  return (
    <div className="flex flex-col justify-center items-center w-[180px] h-[130px] bg-[#96989d] rounded">
      <RxPerson className="text-4xl text-white mb-1" />
      <p className="text-white text-xs text-center">{name}</p>
    </div>
  );
};
